<p-confirm-dialog [draggable]="false" />

@if (loadingForm()) {
    <div class="tw-flex tw-justify-center tw-py-6">
        <p-progress-spinner styleClass="p-progress-spinner-lg" strokeWidth="4" />
    </div>
} @else {
    <mon-page-layout
        [header]="'admin.jiraConnector.header' | transloco"
        [formLayout]="'single-column'"
    >
        @if (!showForm()) {
            <div class="tw-mb-6">
                <p-button
                    data-test="admin-jira-connector-setup-button"
                    [label]="'admin.jiraConnector.emptyState.setupButtonLabel' | transloco"
                    (onClick)="onSetupClick()"
                />
            </div>
        }

        @if (showForm()) {
            <form #form="ngForm" (ngSubmit)="onSubmit(form)" class="admin-jira-connector-form">
            @if (formErrorState() && formErrorState()!.show) {
                <div class="tw-mb-6" data-test="jira-form-error-card">
                    <p-message severity="error" [closable]="false">
                        <div class="tw-flex tw-flex-col tw-gap-3">
                            <strong>{{ formErrorState()!.generalMessage }}</strong>
                            @if (formErrorState()!.fieldErrorMessages.length > 0) {
                                <div>
                                    <p class="tw-mb-2 tw-font-semibold">{{ 'admin.jiraConnector.errors.fieldErrorsLabel' | transloco }}</p>
                                    <ul class="tw-list-disc tw-pl-5 tw-m-0">
                                        @for (errorMsg of formErrorState()!.fieldErrorMessages; track errorMsg) {
                                            <li [attr.data-test]="'jira-form-error-item-' + $index">{{ errorMsg }}</li>
                                        }
                                    </ul>
                                </div>
                            }
                        </div>
                    </p-message>
                </div>
            }


            <!-- Test Result Display -->
            @if (testResponse() && testResponse()!.success && testResponse()!.url) {
                <div class="tw-mb-6">
                    <p-message severity="success" data-test="jira-test-result-message">
                        <div class="tw-flex tw-flex-col tw-gap-2">
                            <span>{{ 'admin.jiraConnector.test.resultLabel' | transloco }}</span>
                            <a
                                [href]="testResponse()!.url"
                                target="_blank"
                                class="tw-text-blue-600 hover:tw-underline"
                                data-test="jira-test-result-link"
                            >
                                {{ testResponse()!.url }}
                                <i class="pi pi-external-link tw-ml-1"></i>
                            </a>
                        </div>
                    </p-message>
                </div>
            }

            <div class="tw-mb-6">
                <p-card>
                    <h2 class="form-header tw-mt-0 tw-mb-4">
                        {{ 'admin.jiraConnector.form.configurationCard.header' | transloco }}
                    </h2>

                    <!-- Base URL -->
                    <div class="tw-flex tw-flex-col tw-gap-2 tw-mb-4">
                        <label class="tw-mb-0" for="base-url">
                            {{ 'admin.jiraConnector.form.baseUrlLabel' | transloco }}
                            <span class="tw-text-red-500">*</span>
                        </label>
                        <input
                            pInputText
                            id="base-url"
                            name="baseUrl"
                            [(ngModel)]="settings.base_url"
                            (ngModelChange)="onFormFieldChange()"
                            required
                            [placeholder]="'admin.jiraConnector.form.baseUrlPlaceholder' | transloco"
                            #baseUrl="ngModel"
                            data-test="jira-base-url-input"
                            [ngClass]="{'ng-invalid': baseUrl.invalid, 'ng-dirty': (baseUrl.dirty || baseUrl.touched)}"
                        />
                        @if (baseUrl.errors?.['required'] && (baseUrl.dirty || baseUrl.touched)) {
                            <small class="tw-text-red-500">
                                {{ 'admin.jiraConnector.form.baseUrlRequired' | transloco }}
                            </small>
                        }
                        @if (baseUrl.errors?.['serverError'] && baseUrl.touched) {
                            <small class="tw-text-red-500" data-test="jira-base-url-server-error">
                                {{ fieldErrors()['baseUrl'] }}
                            </small>
                        }
                    </div>

                    <!-- Username -->
                    <div class="tw-flex tw-flex-col tw-gap-2 tw-mb-4">
                        <label class="tw-mb-0" for="username">
                            {{ 'admin.jiraConnector.form.usernameLabel' | transloco }}
                            <span class="tw-text-red-500">*</span>
                        </label>
                        <input
                            pInputText
                            id="username"
                            name="username"
                            [(ngModel)]="settings.username"
                            (ngModelChange)="onFormFieldChange()"
                            required
                            [placeholder]="'admin.jiraConnector.form.usernamePlaceholder' | transloco"
                            #username="ngModel"
                            data-test="jira-username-input"
                            [ngClass]="{'ng-invalid': username.invalid, 'ng-dirty': (username.dirty || username.touched)}"
                        />
                        @if (username.errors?.['required'] && (username.dirty || username.touched)) {
                            <small class="tw-text-red-500">
                                {{ 'admin.jiraConnector.form.usernameRequired' | transloco }}
                            </small>
                        }
                        @if (username.errors?.['serverError'] && username.touched) {
                            <small class="tw-text-red-500" data-test="jira-username-server-error">
                                {{ fieldErrors()['username'] }}
                            </small>
                        }
                    </div>

                    <!-- API Token -->
                    <div class="tw-flex tw-flex-col tw-gap-2 tw-mb-4">
                        <label class="tw-mb-0" for="api-token">
                            {{ 'admin.jiraConnector.form.apiTokenLabel' | transloco }}
                            @if (mode() === 'create') {
                                <span class="tw-text-red-500">*</span>
                            }
                        </label>
                        <input
                            pInputText
                            id="api-token"
                            name="apiToken"
                            type="text"
                            [(ngModel)]="settings.api_token"
                            (ngModelChange)="onApiTokenChange()"
                            [required]="mode() === 'create'"
                            [placeholder]="'admin.jiraConnector.form.apiTokenPlaceholder' | transloco"
                            #apiToken="ngModel"
                            data-test="jira-api-token-input"
                            [ngClass]="{'ng-invalid': apiToken.invalid, 'ng-dirty': (apiToken.dirty || apiToken.touched)}"
                        />
                        @if (apiToken.errors?.['required'] && (apiToken.dirty || apiToken.touched)) {
                            <small class="tw-text-red-500">
                                {{ 'admin.jiraConnector.form.apiTokenRequired' | transloco }}
                            </small>
                        }
                        @if (mode() === 'edit' && apiTokenTouched() && !settings.api_token?.trim()) {
                            <small class="tw-text-red-500">
                                {{ 'admin.jiraConnector.form.apiTokenRequired' | transloco }}
                            </small>
                        }
                        @if (apiToken.errors?.['serverError'] && apiToken.touched) {
                            <small class="tw-text-red-500" data-test="jira-api-token-server-error">
                                {{ fieldErrors()['apiToken'] }}
                            </small>
                        }
                    </div>

                    <!-- Project Key -->
                    <div class="tw-flex tw-flex-col tw-gap-2 tw-mb-4">
                        <label class="tw-mb-0" for="project-key">
                            {{ 'admin.jiraConnector.form.projectKeyLabel' | transloco }}
                            <span class="tw-text-red-500">*</span>
                        </label>
                        <input
                            pInputText
                            id="project-key"
                            name="projectKey"
                            [(ngModel)]="settings.project_key"
                            (ngModelChange)="onFormFieldChange()"
                            required
                            [placeholder]="'admin.jiraConnector.form.projectKeyPlaceholder' | transloco"
                            #projectKey="ngModel"
                            data-test="jira-project-key-input"
                            [ngClass]="{'ng-invalid': projectKey.invalid, 'ng-dirty': (projectKey.dirty || projectKey.touched)}"
                        />
                        @if (projectKey.errors?.['required'] && (projectKey.dirty || projectKey.touched)) {
                            <small class="tw-text-red-500">
                                {{ 'admin.jiraConnector.form.projectKeyRequired' | transloco }}
                            </small>
                        }
                        @if (projectKey.errors?.['serverError'] && projectKey.touched) {
                            <small class="tw-text-red-500" data-test="jira-project-key-server-error">
                                {{ fieldErrors()['projectKey'] }}
                            </small>
                        }
                    </div>

                    <!-- Issue Type -->
                    <div class="tw-flex tw-flex-col tw-gap-2 tw-mb-4">
                        <label class="tw-mb-0" for="issue-type">
                            {{ 'admin.jiraConnector.form.issueTypeLabel' | transloco }}
                            <span class="tw-text-red-500">*</span>
                        </label>
                        <input
                            pInputText
                            id="issue-type"
                            name="issueType"
                            [(ngModel)]="settings.issue_type"
                            (ngModelChange)="onFormFieldChange()"
                            required
                            [placeholder]="'admin.jiraConnector.form.issueTypePlaceholder' | transloco"
                            #issueType="ngModel"
                            data-test="jira-issue-type-input"
                            [ngClass]="{'ng-invalid': issueType.invalid, 'ng-dirty': (issueType.dirty || issueType.touched)}"
                        />
                        @if (issueType.errors?.['required'] && (issueType.dirty || issueType.touched)) {
                            <small class="tw-text-red-500">
                                {{ 'admin.jiraConnector.form.issueTypeRequired' | transloco }}
                            </small>
                        }
                        @if (issueType.errors?.['serverError'] && issueType.touched) {
                            <small class="tw-text-red-500" data-test="jira-issue-type-server-error">
                                {{ fieldErrors()['issueType'] }}
                            </small>
                        }
                    </div>
                </p-card>
            </div>

            <!-- Custom Fields Card -->
            <div class="tw-mb-6">
                <p-card>
                    <h2 class="form-header tw-mt-0 tw-mb-2">
                        {{ 'admin.jiraConnector.form.customFieldsCard.header' | transloco }}
                    </h2>
                    <p class="tw-text-gray-600 tw-mb-4">
                        {{ 'admin.jiraConnector.form.customFieldsCard.description' | transloco }}
                    </p>

                    @for (field of customFieldsArray; track $index) {
                        <div class="tw-flex tw-gap-4 tw-mb-4">
                            <!-- Field Name -->
                            <div class="tw-flex-1 tw-flex tw-flex-col tw-gap-2">
                                <label class="tw-mb-0" [for]="'custom-field-key-' + $index">
                                    {{ 'admin.jiraConnector.form.customFieldKeyLabel' | transloco }}
                                </label>
                                <input
                                    pInputText
                                    [id]="'custom-field-key-' + $index"
                                    [name]="'customFieldKey' + $index"
                                    [(ngModel)]="customFieldsArray[$index].key"
                                    (ngModelChange)="onFormFieldChange()"
                                    [placeholder]="'admin.jiraConnector.form.customFieldKeyPlaceholder' | transloco"
                                    [attr.data-test]="'jira-custom-field-key-input-' + $index"
                                    #customFieldKey="ngModel"
                                    [ngClass]="{'ng-invalid': customFieldKey.invalid, 'ng-dirty': (customFieldKey.dirty || customFieldKey.touched)}"
                                />
                                @if (hasDuplicateKey($index)) {
                                    <small class="tw-text-red-500">
                                        {{ 'admin.jiraConnector.form.duplicateKeyError' | transloco }}
                                    </small>
                                }
                                @if (customFieldKey.errors?.['serverError'] && customFieldKey.touched) {
                                    <small class="tw-text-red-500" [attr.data-test]="'jira-custom-field-key-server-error-' + $index">
                                        {{ fieldErrors()['customFieldKey' + $index] }}
                                    </small>
                                }
                            </div>

                            <!-- Field Value -->
                            <div class="tw-flex-1 tw-flex tw-flex-col tw-gap-2">
                                <label class="tw-mb-0" [for]="'custom-field-value-' + $index">
                                    {{ 'admin.jiraConnector.form.customFieldValueLabel' | transloco }}
                                </label>
                                <input
                                    pInputText
                                    [id]="'custom-field-value-' + $index"
                                    [name]="'customFieldValue' + $index"
                                    [(ngModel)]="customFieldsArray[$index].value"
                                    (ngModelChange)="onFormFieldChange()"
                                    [placeholder]="'admin.jiraConnector.form.customFieldValuePlaceholder' | transloco"
                                    [attr.data-test]="'jira-custom-field-value-input-' + $index"
                                    #customFieldValue="ngModel"
                                    [ngClass]="{'ng-invalid': customFieldValue.invalid, 'ng-dirty': (customFieldValue.dirty || customFieldValue.touched)}"
                                />
                                @if (customFieldValue.errors?.['serverError'] && customFieldValue.touched) {
                                    <small class="tw-text-red-500" [attr.data-test]="'jira-custom-field-value-server-error-' + $index">
                                        {{ fieldErrors()['customFieldValue' + $index] }}
                                    </small>
                                }
                            </div>

                            <!-- Remove Button -->
                            <div class="tw-flex tw-items-end tw-pb-2">
                                <p-button
                                    icon="pi pi-trash"
                                    severity="danger"
                                    variant="text"
                                    (onClick)="removeCustomField($index)"
                                    [attr.data-test]="'jira-custom-field-remove-button-' + $index"
                                />
                            </div>
                        </div>
                    }

                    <!-- Add Field Button -->
                    <div class="tw-mt-4">
                        <p-button
                            icon="pi pi-plus"
                            [label]="'admin.jiraConnector.form.addCustomFieldButton' | transloco"
                            variant="outlined"
                            (onClick)="addCustomField()"
                            data-test="jira-custom-field-add-button"
                        />
                    </div>
                </p-card>
            </div>

            <!-- Form Footer with Buttons -->
            <div class="form-actions">
                <div class="tw-flex-1"></div>
                @if (settingsExist()) {
                    <p-button
                        data-test="admin-jira-connector-delete-button"
                        [label]="'admin.jiraConnector.form.deleteButtonLabel' | transloco"
                        variant="text"
                        severity="danger"
                        [disabled]="savingForm() || testingConnection() || deletingSettings()"
                        (onClick)="onDelete()"
                    />
                }
                <p-button
                    data-test="admin-jira-connector-test-button"
                    [label]="'admin.jiraConnector.form.testButtonLabel' | transloco"
                    variant="outlined"
                    [loading]="testingConnection()"
                    [disabled]="!settingsExist() || savingForm() || deletingSettings() || form.dirty"
                    styleClass="tw-ml-4"
                    (onClick)="onTest()"
                    [pTooltip]="form.dirty ? ('admin.jiraConnector.form.testButtonTooltip' | transloco) : ''"
                />
                <p-button
                    data-test="admin-jira-connector-save-button"
                    type="submit"
                    [label]="'admin.jiraConnector.form.saveButtonLabel' | transloco"
                    [loading]="savingForm()"
                    [disabled]="form.invalid || testingConnection() || deletingSettings() || (mode() === 'edit' && apiTokenTouched() && !settings.api_token?.trim())"
                    styleClass="tw-ml-4"
                />
            </div>
        </form>
        }
    </mon-page-layout>
}
